/**
 * Payment Platform Context
 * 
 * Provides unified payment method based on platform detection
 * Manages subscription state synchronization across platforms
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  getPlatform,
  isNativePlatform,
  isIOS,
  isAndroid,
  isWeb,
  Platform
} from '../services/platformService';
import {
  initializeRevenueCat,
  setRevenueCatUserId,
  logOutRevenueCat,
  getCustomerInfo,
  getCurrentTier,
  addCustomerInfoListener,
  isRevenueCatReady,
  reinitializeRevenueCat,
  getRevenueCatError,
} from '../services/revenueCatService';
// Local type to avoid static import of native-only package
type CustomerInfo = any;

export type PaymentMethod = 'paypal' | 'apple' | 'google';

interface PaymentPlatformContextType {
  platform: Platform;
  paymentMethod: PaymentMethod;
  isNative: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  nativeTier: 'free' | 'standard' | 'premium' | 'ultimate' | null;
  customerInfo: CustomerInfo | null;
  initializationError: Error | null;
  refreshSubscriptionStatus: () => Promise<void>;
  retryInitialization: () => Promise<void>;
}

const PaymentPlatformContext = createContext<PaymentPlatformContextType | null>(null);

interface PaymentPlatformProviderProps {
  children: ReactNode;
}

const MAX_INIT_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const PaymentPlatformProvider: React.FC<PaymentPlatformProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [nativeTier, setNativeTier] = useState<'free' | 'standard' | 'premium' | 'ultimate' | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [initializationError, setInitializationError] = useState<Error | null>(null);
  const initAttemptRef = useRef(0);
  const isInitializingRef = useRef(false);

  const platform = getPlatform();
  const isNative = isNativePlatform();

  // Determine payment method based on platform
  const paymentMethod: PaymentMethod = (() => {
    if (isIOS()) return 'apple';
    if (isAndroid()) return 'google';
    return 'paypal';
  })();

  // Core initialization function with retry logic
  const initializePayments = useCallback(async (retryCount = 0): Promise<boolean> => {
    if (!isNative) {
      setIsInitialized(true);
      setIsLoading(false);
      return true;
    }

    // 🛡️ DIRECTIVA: No romper iOS. 
    // Solo bloqueamos si no hay ID, para que Android no inicialice en "Anónimo".
    if (!user?.id) {
      console.log('PaymentPlatform: Esperando UUID de Supabase antes de configurar RevenueCat...');
      return false;
    }

    // Si ya estamos listos con este usuario, no hacemos nada (Evita re-configuraciones)
    if (isInitialized && isRevenueCatReady()) return true;

    // Prevent concurrent initialization
    if (isInitializingRef.current) {
      console.log('PaymentPlatform: Initialization already in progress');
      return false;
    }

    isInitializingRef.current = true;
    setIsLoading(true);
    setInitializationError(null);

    try {
      console.log(`PaymentPlatform: Initialization attempt ${retryCount + 1}/${MAX_INIT_RETRIES}`);

      await initializeRevenueCat(user?.id);

      if (user?.id) {
        await setRevenueCatUserId(user.id);
      }

      // Verify RevenueCat is truly ready
      if (isRevenueCatReady()) {
        console.log('PaymentPlatform: RevenueCat initialized successfully');
        setIsInitialized(true);
        initAttemptRef.current = 0;

        // Get initial subscription status
        try {
          const tier = await getCurrentTier();
          setNativeTier(tier);

          const info = await getCustomerInfo();
          setCustomerInfo(info);
        } catch (statusError) {
          console.warn('PaymentPlatform: Could not fetch initial status:', statusError);
        }

        return true;
      } else {
        // Check for specific error
        const error = getRevenueCatError();
        throw error || new Error('RevenueCat init completed but SDK not ready');
      }
    } catch (error) {
      console.error(`PaymentPlatform: Initialization failed (attempt ${retryCount + 1}):`, error);

      const errorInstance = error instanceof Error ? error : new Error(String(error));
      setInitializationError(errorInstance);
      setIsInitialized(false);

      // Retry if we haven't exceeded max attempts
      if (retryCount < MAX_INIT_RETRIES - 1) {
        console.log(`PaymentPlatform: Retrying in ${RETRY_DELAY_MS}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        isInitializingRef.current = false;
        return initializePayments(retryCount + 1);
      }

      return false;
    } finally {
      isInitializingRef.current = false;
      setIsLoading(false);
    }
  }, [user?.id, isNative]);

  // Initialize RevenueCat when user is available
  useEffect(() => {
    initializePayments();
  }, [initializePayments]);

  // Listen for subscription changes on native platforms
  useEffect(() => {
    if (!isNative || !isInitialized) return;

    addCustomerInfoListener((info) => {
      console.log('Customer info updated:', info);
      setCustomerInfo(info);

      // Update tier based on new customer info
      getCurrentTier().then(setNativeTier);
    });
  }, [isNative, isInitialized]);

  // Handle user logout
  useEffect(() => {
    if (!user && isNative && isInitialized) {
      logOutRevenueCat().catch(console.error);
    }
  }, [user, isNative, isInitialized]);

  const refreshSubscriptionStatus = useCallback(async () => {
    if (!isNative || !isRevenueCatReady()) return;

    try {
      setIsLoading(true);
      const tier = await getCurrentTier();
      setNativeTier(tier);

      const info = await getCustomerInfo();
      setCustomerInfo(info);
    } catch (error) {
      console.error('Failed to refresh subscription status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isNative]);

  const retryInitialization = useCallback(async () => {
    if (!isNative) return;

    console.log('PaymentPlatform: Manual retry requested');
    setInitializationError(null);

    // Use reinitializeRevenueCat for a fresh start
    try {
      await reinitializeRevenueCat(user?.id);

      if (isRevenueCatReady()) {
        setIsInitialized(true);

        const tier = await getCurrentTier();
        setNativeTier(tier);

        const info = await getCustomerInfo();
        setCustomerInfo(info);
      } else {
        const error = getRevenueCatError();
        setInitializationError(error);
      }
    } catch (error) {
      console.error('PaymentPlatform: Manual retry failed:', error);
      setInitializationError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [isNative, user?.id]);

  const value: PaymentPlatformContextType = {
    platform,
    paymentMethod,
    isNative,
    isInitialized,
    isLoading,
    nativeTier,
    customerInfo,
    initializationError,
    refreshSubscriptionStatus,
    retryInitialization,
  };

  return (
    <PaymentPlatformContext.Provider value={value}>
      {children}
    </PaymentPlatformContext.Provider>
  );
};

export const usePaymentPlatform = (): PaymentPlatformContextType => {
  const context = useContext(PaymentPlatformContext);
  if (!context) {
    throw new Error('usePaymentPlatform must be used within a PaymentPlatformProvider');
  }
  return context;
};
